import { Link } from "react-router-dom";
import JsonLd from "@/components/base/JsonLd";
import SeoHead from "@/components/feature/SeoHead";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubmitForm from "@/pages/submit/components/SubmitForm";
import { contentPrinciples, siteInfo, submitSteps } from "@/mocks/site";

export default function Submit() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "我要爆料",
    description:
      "通过匿名投稿表单或私信通道向爆料专区提供线索，支持匿名提交，联系方式仅用于核实。",
    url: `${origin}/submit`,
  };

  return (
    <div className="bg-background-100">
      <SeoHead
        title="我要爆料 - 匿名投稿与私信通道 | 爆料专区"
        description="在爆料专区匿名投稿，填写爆料内容与分类即可提交。也支持通过私信通道补充图片与录音线索，全程匿名，联系方式仅用于核实。"
        keywords="爆料投稿,匿名爆料,私信通道,投稿入口,爆料专区"
        path="/submit"
      />
      <JsonLd data={schema} />

      <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "我要爆料" }]} />

        <header className="mt-5 max-w-3xl">
          <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">我要爆料</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            这里是投稿与私信两条通道的入口。你不需要实名，也不用写得漂亮——把时间、地点和经过讲清楚，
            编辑会帮你把细节整理成一篇完整的爆料，保护隐私是我们的底线。
          </p>
        </header>

        <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { icon: "ri-user-forbid-line", label: "全程匿名", desc: "不收集真实身份信息" },
            { icon: "ri-file-shield-2-line", label: "隐去个人信息", desc: "发布前统一处理敏感信息" },
            { icon: "ri-time-line", label: "24 小时内整理", desc: "工作时段优先处理" },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className={`${item.icon} text-lg`} aria-hidden="true" />
              </span>
              <div>
                <p className="font-label text-sm font-medium text-foreground-900">{item.label}</p>
                <p className="font-label text-xs text-foreground-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <SubmitForm />
          </div>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-display text-base text-foreground-950">投稿流程</h2>
              <ol className="mt-4 flex flex-col gap-5">
                {submitSteps.map((step, index) => (
                  <li key={step.title} className="flex gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 font-label text-sm text-primary-700">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-label text-sm font-medium text-foreground-900">{step.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section
              id="private-message"
              className="rounded-lg border border-accent-200 bg-accent-50 p-5"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                <i className="ri-chat-private-line text-lg" aria-hidden="true" />
              </span>
              <h2 className="mt-4 font-display text-base text-foreground-950">私信通道</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                手上有图片、录音或长文档，不方便写进表单？直接把材料发到下面的邮箱，注明大致时间与地点即可，
                我们不会公开你的任何联系方式。
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                rel="nofollow"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-4 py-2.5 font-label text-sm text-accent-900 transition-colors hover:bg-accent-100"
              >
                <i className="ri-mail-send-line" aria-hidden="true" />
                {siteInfo.email}
              </a>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-display text-base text-foreground-950">我们会怎么处理</h2>
              <ul className="mt-4 flex flex-col gap-4">
                {contentPrinciples.map((item) => (
                  <li key={item.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-700">
                      <i className={`${item.icon} text-base`} aria-hidden="true" />
                    </span>
                    <div>
                      <p className="font-label text-sm font-medium text-foreground-900">{item.title}</p>
                      <p className="mt-0.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap font-label text-sm text-primary-700 transition-colors hover:text-primary-800"
              >
                了解完整内容原则
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}